import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { api, errMsg } from "./lib/api";
import { Loader } from "./components/common";
import { ContactOnboarding, needsContact } from "./components/ContactOnboarding";
import AppShell from "./components/AppShell";
import Landing from "./pages/Landing";
import Pending from "./pages/Pending";
import Dashboard from "./pages/Dashboard";
import Stages from "./pages/Stages";
import StageDetail from "./pages/StageDetail";
import Athletes from "./pages/Athletes";
import AthleteProfile from "./pages/AthleteProfile";
import Ranking from "./pages/Ranking";
import Charges from "./pages/Charges";
import Polls from "./pages/Polls";
import Giveaways from "./pages/Giveaways";
import Admin from "./pages/Admin";
import Store from "./pages/Store";
import Seasons from "./pages/Seasons";
import Notifications from "./pages/Notifications";
import More from "./pages/More";

const AuthCallback = () => {
  const done = useRef(false);
  const navigate = useNavigate();
  const { setUser } = useAuth();
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: sid })
      .then(({ data }) => {
        setUser(data);
        window.history.replaceState(null, "", window.location.pathname);
        navigate(data.status === "approved" ? "/app" : "/aguardando", { replace: true });
      })
      .catch((e) => {
        toast.error(errMsg(e));
        navigate("/", { replace: true });
      });
  }, [navigate, setUser]);
  return <Loader full />;
};

const Protected = ({ children, admin }) => {
  const { user, loading } = useAuth();
  if (loading) return <Loader full />;
  if (!user) return <Navigate to="/" replace />;
  if (user.status !== "approved") return <Navigate to="/aguardando" replace />;
  if (needsContact(user)) return <ContactOnboarding />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  return children;
};

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/aguardando" element={<Pending />} />
      <Route path="/app" element={<Protected><AppShell /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="etapas" element={<Stages />} />
        <Route path="etapas/:id" element={<StageDetail />} />
        <Route path="atletas" element={<Athletes />} />
        <Route path="atletas/:id" element={<AthleteProfile />} />
        <Route path="ranking" element={<Ranking />} />
        <Route path="cobrancas" element={<Charges />} />
        <Route path="votacao" element={<Polls />} />
        <Route path="brindes" element={<Giveaways />} />
        <Route path="mais" element={<More />} />
        <Route path="loja" element={<Store />} />
        <Route path="temporadas" element={<Seasons />} />
        <Route path="notificacoes" element={<Notifications />} />
        <Route path="admin" element={<Protected admin><Admin /></Protected>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster theme="dark" position="top-center" richColors />
    </AuthProvider>
  );
}
